import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import APIs from "../../APIs";
import { useDispatch, useSelector } from "react-redux";
import { setAddresses, setPickupAddress, setDropoffAddress } from "../../redux/addressSlice";
import Select from "react-select";
import { toast } from "react-toastify";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAP_KEY } from "../../Utils/Utils";
import { useMyLocation } from "../../hooks/LocationContext";

function Address() {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    landmark: "",
    country: "",
    state: "",
    city: "",
    zip_code: "",
    address_type: "",
  });
    const { location: currentLocation, loaded } = useMyLocation();
    console.log("current my location1", currentLocation);
  const [coordinates, setCoordinates] = useState({ lat: currentLocation.lat, lng: currentLocation.lng }); // Default Delhi
  const [map, setMap] = useState(null);
  const geocoderRef = useRef(null);

  const [message, setMessage] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const addresses = useSelector((state) => state.address.addresses);
  const pickupAddress = useSelector((state) => state.address.pickupAddress);
  const dropoffAddress = useSelector((state) => state.address.dropoffAddress);
  const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
  const user_id = userData?.user_id;

  const [user, setUser] = useState({});
  const params = new URLSearchParams(location.search);
  const type = params.get("type") || "pickup";

  // ✅ Load Google Maps
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
  });

  // ✅ Initialize Geocoder
  useEffect(() => {
    if (isLoaded && window.google && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!user_id) return;
      try {
        const res = await APIs.post(
          "user/getUserById",
          { user_id },
          { headers: { "Content-Type": "application/json" } }
        );
        setUser(res.data?.data || {});
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, [user_id]);

  // ✅ Fetch addresses on mount
  useEffect(() => {
    getAddress();
  }, []);

  const getAddress = async () => {
    try {
      if (!user_id) return;
      const res = await APIs.post("address/getAddressByUser", { user_id });
      const dataArray = Array.isArray(res.data.data) ? res.data.data : [];
      dispatch(setAddresses(dataArray));
    } catch (error) {
      console.error("Error fetching addresses:", error);
      dispatch(setAddresses([]));
    }
  };

  // ✅ Reverse Geocode to auto-fill address fields
  const fetchAddress = useCallback((lat, lng) => {
    if (!geocoderRef.current) return;
    const location = { lat: parseFloat(lat), lng: parseFloat(lng) };

    geocoderRef.current.geocode({ location }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        const components = place.address_components;
        let city = "", state = "", country = "", postalCode = "", landmark = "";

        components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality")) city = component.long_name;
          if (types.includes("administrative_area_level_1")) state = component.long_name;
          if (types.includes("country")) country = component.long_name;
          if (types.includes("postal_code")) postalCode = component.long_name;
          if (types.includes("sublocality") || types.includes("neighborhood"))
            landmark = component.long_name;
        });

        const shortAddress = place.formatted_address.split(",").slice(0, 2).join(",");

        setFormData((prev) => ({
          ...prev,
          address: shortAddress || prev.address,
          city: city || prev.city,
          state: state || prev.state,
          country: country || prev.country,
          zip_code: postalCode || prev.zip_code,
          landmark: landmark || prev.landmark,
        }));
      } else {
        toast.error("Unable to get address for this location");
      }
    });
  }, []);

  const handleCenterChanged = () => {
    if (!map) return;
    const center = map.getCenter();
    const lat = center.lat();
    const lng = center.lng();
    setCoordinates({ lat, lng });
    fetchAddress(lat, lng);
  };

  const handleMapLoad = (mapInstance) => {
    setMap(mapInstance);
  };
  async function getCoordinatesFromAddress(address) {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK") {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      } else {
        console.error("Geocode API Error:", data.status);
        toast.error("Unable to find location for this address");
        return { lat: 0, lng: 0 };
      }
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      toast.error("Error fetching coordinates");
      return { lat: 0, lng: 0 };
    }
  }
  // ✅ Add validation function
const validateForm = () => {
  const newErrors = {};
  if (!formData.address.trim()) newErrors.address = "Address is required";
  if (!formData.landmark.trim()) newErrors.landmark = "Landmark is required";
  if (!formData.city.trim()) newErrors.city = "City is required";
  if (!formData.state.trim()) newErrors.state = "State is required";
  if (!formData.country.trim()) newErrors.country = "Country is required";
  if (!formData.zip_code.trim()) newErrors.zip_code = "Zip Code is required";
  if (!formData.address_type.trim()) newErrors.address_type = "Address type is required";
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!user_id) {
    toast.error("User not logged in!");
    return;
  }

  if (!validateForm()) {
    toast.error("Please fill all required fields");
    return;
  }

  setLoading(true);

  try {
    // ✅ Combine form fields to form a complete manual address
    const fullAddress = `${formData.address}, ${formData.city}, ${formData.state}, ${formData.country}, ${formData.zip_code}`;

    // ✅ Get lat/lng from Google Geocoding API
    const { lat, lng } = await getCoordinatesFromAddress(fullAddress);

    const payload = {
      user_id,
      address_lat: lat,
      address_long: lng,
      ...formData,
    };

    const res = await APIs.post("address/addAddress", payload);

    if (res.data.status === "success") {
      toast.success("Address added successfully!");
      setFormData({
        address: "",
        landmark: "",
        country: "",
        state: "",
        city: "",
        zip_code: "",
        address_type: "",
      });
      setErrors({});
      getAddress();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("addAddressModalToggle")
      );
      if (modal) modal.hide();

      window.location.href = `/address?type=${type}`;
    } else {
      toast.error("Failed to add address");
    }
  } catch (err) {
    console.error("Error adding address:", err);
    toast.error("Error adding address or fetching location");
  } finally {
    setLoading(false);
  }
};



  const handleSelectChange = (selectedOption) => {
    const selectedId = selectedOption?.value;
    const selectedAddr = addresses.find(
      (addr) => String(addr.id || addr.address_id) === selectedId
    );
    if (!selectedAddr) return;

    if (type === "pickup") {
      dispatch(setPickupAddress(selectedAddr));
    } else {
      dispatch(setDropoffAddress(selectedAddr));
    }
    navigate("/Step2");
  };

  const filteredAddresses = addresses.filter((addr) => {
    if (type === "dropoff" && pickupAddress) {
      const pickupId = pickupAddress.id || pickupAddress.address_id;
      const currentId = addr.id || addr.address_id;
      return pickupId !== currentId;
    }
    return true;
  });

  const addressOptions = useMemo(
    () =>
      filteredAddresses.map((addr) => ({
        value: String(addr.id || addr.address_id),
        label: `${addr.address || addr.full_address}, ${addr.zip_code}`,
      })),
    [filteredAddresses]
  );

  const handleCloseModal = (modalId) => {
    const modalEl = document.getElementById(modalId);
    const modalInstance =
      window.bootstrap.Modal.getInstance(modalEl) ||
      new window.bootstrap.Modal(modalEl);
    modalInstance.hide();
    window.location.reload();
  };

  if (loadError) return <p>Error loading Google Maps!</p>;
  if (!isLoaded) return <p>Loading map...</p>;



  return (
    <div className="page-wrapper about-us">
      <div className="page-content">
        <section className="step-section-md">
          <div className="container">
            <div className="row">
              {/* Left Column */}
              <div className="col-lg-6">
                <div className="shipment-information-new">
                  <div className="step-header">
                    <Link to="/">
                      <img src="/images/logo1.jpg" alt="Logo" className="img-fluid" />
                    </Link>
                    {user && Object.keys(user).length > 0 ? (
                      <div className="align-items-center d-flex gap-1">
                        <img
                          src={user.profile_image || "/images/user.png"}
                          alt="Profile"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginLeft: "25px",
                          }}
                        />
                        <span>{user.full_name || ""}</span>
                      </div>
                    ) : (
                      <a data-bs-toggle="modal" href="#LoginModalToggle">
                        <i className="eagle-base-icon-user"></i> Sign In
                      </a>
                    )}
                  </div>

                  <div className="new-title">
                    <h3>{type === "pickup" ? "Select Pickup Address" : "Select Drop-Off Address"}</h3>
                    <p>Choose from saved addresses or add a new one to continue your booking.</p>
                    <h6>Why Choose Us</h6>
                      <div className="about__list-box about__list-box-two">
                          <ul className="list-wrap">
                              <li><i className="flaticon-check"></i>Ultra-Fast Delivery: Next-flight-out (NFO) options worldwide.</li>
                              <li><i className="flaticon-check"></i>Freelance Couriers: Global network of experienced OBC professionals.</li>
                              <li><i className="flaticon-check"></i>Digital Convenience: Book, quote, and track—all online.</li>
                              <li><i className="flaticon-check"></i>Secure &amp; Reliable: Each shipment is escorted personally.</li>
                          </ul>
                      </div>
                  </div>
                  <img src="images/banner-slider-img/3.png" alt="Banner" className="img-fluid" />
                </div>
              </div>

              {/* Right Column */}
              <div className="col-lg-6">
                <div className="shipment-details-new">
                  <h3 className="new-title">{type === "pickup" ? "Pickup Address" : "Drop-Off Address"}</h3>

                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={addressOptions}
                    placeholder="Select Address"
                    value={
                      type === "pickup" && pickupAddress
                        ? {
                            value: pickupAddress.address_id,
                            label: `${pickupAddress.address}, ${pickupAddress.city}, ${pickupAddress.state}, ${pickupAddress.zip_code}`,
                            data: pickupAddress,
                          }
                        : type === "dropoff" && dropoffAddress
                        ? {
                            value: dropoffAddress.address_id,
                            label: `${dropoffAddress.address}, ${dropoffAddress.city}, ${dropoffAddress.state}, ${dropoffAddress.zip_code}`,
                            data: dropoffAddress,
                          }
                        : null
                    }
                    onChange={handleSelectChange}
                    isClearable
                    styles={{
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected
                          ? "#54bf66" // selected option background
                          : state.isFocused
                          ? "#54bf66" // hover background
                          : "white",  // default background
                        color: state.isSelected ? "white" : "black",
                        cursor: "pointer",
                      }),
                      control: (provided) => ({
                        ...provided,
                        borderRadius: "8px",
                        borderColor: "#54bf66",
                        boxShadow: "none",
                        "&:hover": { borderColor: "#45a657" },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: "8px",
                        overflow: "hidden",
                      }),
                    }}
                  />

                  <div className="button-wraper-new mt-3">
                    <button type="button" className="btn btn-dark btn-lg" onClick={() => navigate("/Step2")}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-success btn-lg flex-1"
                      data-bs-toggle="modal"
                      data-bs-target="#addAddressModalToggle"
                    >
                      Add New Address
                    </button>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <img src="images/map1.svg" alt="Map" className="img-fluid" />
                  <h5>How To Search</h5>
                  <p>Enter an address above and select from the results.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ✅ Add Address Modal with Google Map */}
      <div
        className="modal fade commen-popup"
        id="addAddressModalToggle"
        tabIndex="-1"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="row">
                {/* Left side — Map */}
                <div className="col-lg-6">
                  <div
                    style={{
                      width: "100%",
                      height: "400px",
                      borderRadius: "8px",
                      border: "2px solid #54bf66",
                      position: "relative",
                    }}
                  >
                    <GoogleMap
                      center={coordinates}
                      zoom={14}
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      onLoad={handleMapLoad}
                      onDragEnd={handleCenterChanged}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -100%)",
                        fontSize: "30px",
                        zIndex: "10",
                      }}
                    >
                      📍
                    </div>
                  </div>

                  <p className="mt-2 text-muted">
                    <strong>Selected:</strong> {formData.address || "Fetching address..."}
                  </p>
                </div>

                {/* Right side — Form */}
                <div className="col-lg-6">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => handleCloseModal("addAddressModalToggle")}
                  ></button>

                  <h5 className="modal-title">Add New Address</h5>
                  <p>Drag the map to your location or fill in details below.</p>

                  <form className="signup-form" onSubmit={handleSubmit}>
                    <div className="row">
                      {["address", "landmark", "country", "state", "city", "zip_code"].map((field) => (
                        <div className="col-sm-12 col-md-6 mb-2" key={field}>
                          <input
                            type="text"
                            className={`form-control ${errors[field] ? "is-invalid" : ""}`}
                            placeholder={field.charAt(0).toUpperCase() + field.slice(1) + (["address", "country", "state", "city", "zip_code"].includes(field) ? " *" : "")}
                            name={field}
                            value={formData[field]}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                [e.target.name]: e.target.value,
                              }))
                            }
                          />
                          {errors[field] && (
                            <small className="text-danger">{errors[field]}</small>
                          )}
                        </div>
                      ))}

                      <div className="col-sm-12 mb-2">
                        <select
                          className={`form-control select-control ${errors.address_type ? "is-invalid" : ""}`}
                          name="address_type"
                          value={formData.address_type}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, address_type: e.target.value }))
                          }
                        >
                          <option value="">Select Address Type *</option>
                          <option value="Home">Home</option>
                          <option value="Office">Office</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.address_type && (
                          <small className="text-danger">{errors.address_type}</small>
                        )}
                      </div>

                      <div className="col-md-12 mb-3">
                        <button
                          type="submit"
                          className="eagle-btn w-100 text-center"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Save Address"}
                        </button>
                      </div>
                    </div>
                  </form>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Address;
